import React, { useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SidebarBackButton from '../SidebarBackButton/SidebarBackButton';
import { useUISettings } from '../../context/UISettingsContext';
import './SidebarShell.css';

export default function SidebarShell({
  title,
  headerExtras = null,
  showBackButton = true,
  children,
  footer = null,
  className = '',
}) {
  const { sidebarCollapsed, forceSidebarCollapsed, toggleSidebarCollapsed } = useUISettings();
  const { pathname } = useLocation();
  const headerRef = useRef(null);
  // The re-open handle is position:fixed, but the sidebar header sits at a
  // different viewport Y per page (some routes render a banner/topnav above the
  // shell). Measure the header so the handle's vertical band matches it exactly
  // — the control must not shift when toggling. The header stays in the DOM at
  // its Y even when collapsed (the aside is just zero-width), so this works in
  // both states.
  const [handleBox, setHandleBox] = useState({ top: 0, height: 48 });

  useLayoutEffect(() => {
    const measure = () => {
      const el = headerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setHandleBox((prev) =>
        (prev.top === r.top && prev.height === r.height)
          ? prev
          : { top: r.top, height: r.height }
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [sidebarCollapsed, pathname]);

  const asideClasses = ['sidebar-shell', className].filter(Boolean).join(' ');

  return (
    <>
      <aside className={asideClasses}>
        <div className="sidebar-section">
          <div className="sidebar-header" ref={headerRef}>
            <h3>{title}</h3>
            {showBackButton && <SidebarBackButton />}
            {headerExtras}
            {/* In the /play demo the sidebar is pinned collapsed; hide the
                collapse/expand controls so the visitor can't un-pin it (and so a
                toggle never writes the real user's stored preference). */}
            {!forceSidebarCollapsed && (
              <button
                type="button"
                className="sidebar-collapse-btn"
                onClick={toggleSidebarCollapsed}
                aria-label="Collapse sidebar"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M11 4 L7 8 L11 12 M7 4 L3 8 L7 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
          <div className="sidebar-content">
            {children}
          </div>
        </div>
        {footer}
      </aside>
      {/* Fixed re-open handle — co-located with the shell so it exists on every
          sidebar'd page; CSS shows it only while collapsed. position:fixed keeps
          it visible after the aside itself shrinks to zero width; top/height are
          pinned to the measured header band so it never jumps on toggle.
          Suppressed in the /play demo (sidebar is pinned collapsed). */}
      {!forceSidebarCollapsed && (
        <button
          type="button"
          className="sidebar-expand-handle"
          style={{ top: `${handleBox.top}px`, height: `${handleBox.height}px` }}
          onClick={toggleSidebarCollapsed}
          aria-label="Expand sidebar"
          aria-hidden={!sidebarCollapsed}
          tabIndex={sidebarCollapsed ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M5 4 L9 8 L5 12 M9 4 L13 8 L9 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </>
  );
}
